import React, { useState, useEffect } from 'react';
import { Zap, User, Server, AlertCircle, History } from 'lucide-react';
import api from '../services/api';
import '../styles/activity.css';

function Activity({ user }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
    const interval = setInterval(loadHistory, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadHistory = async () => {
    try {
      const data = await api.getStatus();
      setHistory(data.history || []);
    } catch (err) {
      // console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (action) => {
    if (!action) return <Zap size={20} />;
    const a = action.toLowerCase();
    if (a.includes('deploy') || a.includes('upload')) return <Zap size={20} />;
    if (a.includes('stop') || a.includes('delete')) return <AlertCircle size={20} />;
    if (a.includes('start') || a.includes('restart')) return <History size={20} />;
    return <Zap size={20} />;
  };

  const getEventLabel = (action) => {
    if (!action) return 'Action';
    const a = action.toLowerCase();
    if (a.includes('deploy') || a.includes('upload')) return 'Application Deployed';
    if (a.includes('stop')) return 'Application Stopped';
    if (a.includes('delete')) return 'Application Removed';
    if (a.includes('start')) return 'Application Started';
    if (a.includes('restart')) return 'Application Restarted';
    if (a.includes('update')) return 'Configuration Updated';
    if (a.includes('backup')) return 'Backup Created';
    if (a.includes('restore')) return 'Backup Restored';
    return action.charAt(0).toUpperCase() + action.slice(1);
  };

  const getEventClass = (action) => {
    if (!action) return '';
    const a = action.toLowerCase();
    if (a.includes('deploy') || a.includes('upload') || a.includes('start') || a.includes('restore')) return 'success';
    if (a.includes('stop') || a.includes('delete')) return 'danger';
    if (a.includes('restart') || a.includes('update')) return 'warning';
    return '';
  };

  const getContextLabel = (entry) => {
    const action = (entry.action || '').toLowerCase();
    if (action.includes('docker_deploy')) return 'Docker Hub';
    if (action.includes('repo_deploy')) {
      const provider = entry.details?.provider || 'GitHub/GitLab';
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
    if (action === 'upload' || action === 'deploy') return 'Direct Upload';
    if (action.includes('delete')) return 'Deleted';
    if (action.includes('update')) return 'Edited';
    if (action.includes('start')) return 'Started';
    if (action.includes('stop')) return 'Stopped';
    if (action.includes('restart')) return 'Restarted';
    return 'Deployment';
  };

  if (loading) {
    return (
      <div className="activity-container">
        <div className="activity-empty">
          <div className="app-loading-dots" style={{ justifyContent: 'center', marginBottom: '1rem' }}>
            <span></span><span></span><span></span>
          </div>
          <p>Loading activity stream...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-container">
      <header className="activity-header">
        <h1 className="activity-title">Activity Logs</h1>
        <p className="activity-subtitle">Real-time stream of events and deployment actions across your infrastructure.</p>
      </header>

      <div className="activity-feed">
        {history.length === 0 ? (
          <div className="activity-empty">
            <History size={48} className="activity-empty-icon" />
            <h3>No activity found</h3>
            <p>Recent events and actions will appear here once they occur.</p>
          </div>
        ) : (
          history.map((entry, idx) => (
            <div key={idx} className={`activity-item ${getEventClass(entry.action)}`}>
              <div className="activity-icon-wrapper">
                {getEventIcon(entry.action)}
              </div>
              
              <div className="activity-content">
                <div className="activity-event-row">
                  <span className="activity-event-name">{getEventLabel(entry.action)}</span>
                  {entry.appName && (
                    <span className="activity-app-badge">
                      {entry.appName}
                    </span>
                  )}
                </div>
                
                <div className="activity-meta">
                  <span className="activity-owner">
                    <User size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {entry.owner || 'system'}
                  </span>
                  <span className="activity-meta-dot"></span>
                  <span className="activity-environment">
                    <Server size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {getContextLabel(entry)}
                  </span>
                </div>
              </div>

              <div className="activity-time-group">
                <span className="activity-time">
                  {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="activity-date">
                  {new Date(entry.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Activity;
